<template>
  <div
    v-if="visualFacetValuesWithImages?.length"
    class="visual-facet"
    :class="{
      'visual-facet--desktop-center': alignment === null && visualFacetValuesWithImages.length < 5,
      'visual-facet--desktop-left': alignment === 'left',
    }"
  >
    <div
      v-if="facet"
      class="visual-facet__container"
    >
      <h3
        class="visually-hidden"
        v-text="facet.label"
      />

      <div
        ref="carousel"
        class="visual-facet__values"
        :js-body-scroll-lock-ignore="true"
        @scroll="handleCarouselScroll"
      >
        <template
          v-for="(facetValue, i) of visualFacetValuesWithImages"
          :key="facetValue.value"
        >
          <div
            v-if="facetValue.image"
            ref="cards"
            class="visual-facet__value"
          >
            <input
              :id="`visual-facet__input--${i}`"
              type="checkbox"
              class="visual-facet__input visually-hidden"
              :aria-label="(
                isFacetActive(facetValue)
              ) ? $string(
                'collection.filters.deselect_value_accessibility_text', {
                  replace: {
                    value: facet.label + ' - ' + facetValue.label
                  }
                }
              ) : $string(
                'collection.filters.select_value_accessibility_text', {
                  replace: {
                    value: facet.label + ' - ' + facetValue.label
                  }
                }
              )"
              :name="facet.field"
              :value="facetValue.value"
              :checked="isFacetActive(facetValue)"
              :disabled="isLoading"
              @focusin="scrollToElement"
              @change.prevent="
                (event) => updateSelectedFacetValue(
                  facet.field,
                  facetValue.value,
                  event.target.checked
                )
              "
            >

            <label
              :for="`visual-facet__input--${i}`"
              class="visual-facet__label"
            >
              <div class="visual-facet__image-wrapper">
                <responsive-image
                  class="visual-facet__image"
                  :image="facetValue.image"
                  :lazyload="false"
                  :padding="false"
                  ratio="4:5"
                  sizes="410px, (min-width: 1024px) 400px"
                />

                <icon-check
                  class="visual-facet__icon"
                  :class="{
                    [$class('active')]: facetValue.active ||
                      (isLoading && draftFacetUpdate === facetValue.value),
                  }"
                />

                <icon-plus class="visual-facet__icon" />
              </div>

              <h4
                class="visual-facet__label-text"
                v-text="facetValue.label"
              />
            </label>
          </div>
        </template>
      </div>
      <template v-if="!isMobile && (visualFacetValuesWithImages.length > 8)">
        <button
          ref="prev"
          class="button product-carousel__btn product-carousel__btn--prev"
          disabled
          type="button"
          :aria-label="$string('accessibility.carousel.previous')"
          @click="moveCarousel('prev')"
        >
          <icon-arrow-left />
        </button>

        <button
          ref="next"
          class="button product-carousel__btn product-carousel__btn--next"
          type="button"
          :aria-label="$string('accessibility.carousel.next')"
          @click="moveCarousel('next')"
        >
          <icon-arrow-right />
        </button>
      </template>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Visual facet (visual-facet)
 * -----------------------------------------------------------------------------
 * A visual facet for SearchSpring filtering.
 * @param {Object} facet - The facet data.
 * @param {Object} isLoading - Whether the SS data is loading.
 *
 */
import { mapState } from 'vuex'
import IconCheck from '~icons/general/check.svg'
import IconPlus from '~icons/directional-navigation/plus.svg'
import IconArrowLeft from '~icons/directional-navigation/arrow/left-small.svg'
import IconArrowRight from '~icons/directional-navigation/arrow/right-small.svg'
import ResponsiveImage from '~global/images/responsive-image'

export default {
  name: 'VisualFacet',

  components: {
    IconCheck,
    IconPlus,
    ResponsiveImage,
    IconArrowLeft,
    IconArrowRight,
  },

  props: {
    facet: {
      type: Object,
      default: null,
    },

    isLoading: {
      type: Boolean,
      required: true,
    },

    alignment: {
      type: String,
      default: null,
    },
  },

  emits: {
    'facet-value-changed': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      draftFacetUpdate: null,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Computes if visual facets contain images
     * @returns {Boolean} - Whether visual facets contain images
     */
    visualFacetValuesWithImages() {
      return this.facet?.values?.filter(value => value.image)
    },
  },

  watch: {
    facet() {
      this.draftFacetUpdate = null
    },
  },

  mounted() {
    this.handleCarouselScroll()
  },

  methods: {

    /**
     * Scrolls element into view if off screen on focusin
     * @param element - Element to scoll to
     */
    scrollToElement(element) {
      const sibling = element?.target?.nextElementSibling
      const clientRect = sibling.getBoundingClientRect()

      if (clientRect.x + 10 >= window.innerWidth ||
        clientRect.x - 10 < 0) {
        sibling.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
      }
    },

    /**
     * Returns if the facet is active
     * @param {Object} facetValue - The facet value
     * @returns {Boolean} Final facets
     */
    isFacetActive(facetValue) {
      return facetValue.active || (this.isLoading && this.draftFacetUpdate === facetValue.value)
    },

    /**
     * Emits an update event for the parent component to update its selections
     */
    updateSelectedFacetValue(field, value, selected) {
      this.draftFacetUpdate = value
      this.$emit('facet-value-changed', {
        field,
        value,
        selected,
      })
    },

    /**
     * Handle scrolling product carousel.
     * @param {String} direction - Direction to move carousel of sign post.
     */
    moveCarousel(direction) {
      const carousel = this.$refs.carousel
      const cards = this.$refs.cards

      if (!carousel || !cards) {
        return
      }

      const cardWidth = cards[0].clientWidth

      carousel.scrollLeft = direction === 'prev' ? carousel.scrollLeft - cardWidth - 1 : carousel.scrollLeft + cardWidth + 1
    },

    /**
     * Handles carousel scroll event to update arrows
     */
    handleCarouselScroll() {
      const carousel = this.$refs.carousel

      if (!carousel) {
        return
      }

      const scrollWidth = carousel.scrollWidth
      const scroll = carousel.scrollLeft
      const carouselWidth = carousel.clientWidth

      const prevButton = this.$refs.prev
      const nextButton = this.$refs.next

      const hideButton = (button) => {
        button?.setAttribute('disabled', true)
      }

      const showButton = (button) => {
        button?.removeAttribute('disabled')
      }

      if (scroll === 0) {
        hideButton(prevButton)
      } else {
        showButton(prevButton)
      }

      if ((scroll + carouselWidth) >= scrollWidth) {
        hideButton(nextButton)
      } else {
        showButton(nextButton)
      }
    },
  },
}
</script>

<style lang="scss">
@import './visual-facet';
</style>
